import moment, { Moment } from 'moment';

// Playground messages are stored untyped in the slice; only these fields drive the timeline.
export interface TimelineMessage {
  created_at?: unknown;
  end_chat?: unknown;
  use_manager?: unknown;
}

export interface DateDivider {
  type: 'date';
  value: string;
}

export const isDateDivider = (item: object): item is DateDivider =>
  (item as DateDivider).type === 'date';

export interface PlaygroundTimeline<T extends TimelineMessage> {
  items: Array<T | DateDivider>;
  escalatedAt: string;
  resolvedAt: string;
}

const getDayLabel = (day: Moment, now: Moment): string => {
  if (now.isSame(day, 'day')) {
    return 'Today';
  }

  if (now.clone().add(-1, 'days').isSame(day, 'day')) {
    return 'Yesterday';
  }

  return day.format('ll');
};

/**
 * Interleaves day dividers between playground messages and derives the escalated/resolved
 * markers. A final `use_manager` message is shown as the escalation marker instead of a bubble,
 * and a closed or escalated chat status overrides the markers with the last item's date.
 */
export const buildPlaygroundTimeline = <T extends TimelineMessage>(
  messages: T[] = [],
  status?: unknown,
  now: Moment = moment(),
): PlaygroundTimeline<T> => {
  const items: Array<T | DateDivider> = [];
  let currentDay: Moment | null = null;
  let escalatedAt = '';
  let resolvedAt = '';

  messages?.forEach((message, index) => {
    const isLastMessage = index === messages.length - 1;
    const createdAt = moment(message.created_at as string);

    if (!currentDay || !createdAt.isSame(currentDay, 'day')) {
      currentDay = createdAt;
      items.push({ type: 'date', value: getDayLabel(currentDay, now) });
    }

    if (isLastMessage && message.end_chat) {
      resolvedAt = message.created_at as string;
    }

    if (isLastMessage && message.use_manager) {
      escalatedAt = message.created_at as string;
      return;
    }

    items.push(message);
  });

  const getLastItemCreatedAt = () => {
    const lastItem = items.at(-1);

    return lastItem && 'created_at' in lastItem ? (lastItem.created_at as string) : undefined;
  };

  switch (status) {
    case 'closed':
    case 'auto_closed':
      escalatedAt = '';
      resolvedAt = getLastItemCreatedAt();
      break;
    case 'escalated':
      resolvedAt = '';
      escalatedAt = getLastItemCreatedAt();
      break;
  }

  return { items, escalatedAt, resolvedAt };
};
